export const agentConfig = {
  // Day keys only; labels are resolved from i18n ("agents.days.<key>").
  DAYS: [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ],

  TIME_OPTIONS: (() => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        opts.push(`${hh}:${mm}`);
      }
    }
    return opts;
  })(),

  // Duration values in minutes; labels are formatted in the component via i18n.
  DURATION_OPTIONS: (() => {
    const opts = [];
    for (let m = 15; m <= 120; m += 15) opts.push(m);
    return opts;
  })(),

  DEFAULT_SCHEDULE: {
    monday: { enabled: true, start_time: "10:00", end_time: "20:00" },
    tuesday: { enabled: true, start_time: "10:00", end_time: "20:00" },
    wednesday: { enabled: true, start_time: "10:00", end_time: "20:00" },
    thursday: { enabled: true, start_time: "10:00", end_time: "20:00" },
    friday: { enabled: true, start_time: "10:00", end_time: "20:00" },
    saturday: { enabled: false, start_time: "10:00", end_time: "20:00" },
    sunday: { enabled: false, start_time: "10:00", end_time: "20:00" },
  },

  // Step keys + icons; labels are resolved from i18n ("agents.steps.<key>").
  WIZARD_STEPS: [
    { key: "org", icon: "corporate_fare" },
    { key: "schedule", icon: "schedule" },
    { key: "appointments", icon: "event_available" },
    { key: "greeting", icon: "chat" },
  ],

  // ── Model / provider dropdown values ──
  // Must be kept in sync with the `Literal[...]` fields in
  // the LangGraph/LiveKit DeploymentConfig models and the backend agent-card
  // schema.
  STT_PROVIDER_OPTIONS: [{ value: "elevenlabs", label: "ElevenLabs" }],
  STT_MODEL_OPTIONS: [
    { value: "scribe_v2_realtime", label: "Scribe v2 Realtime" },
  ],
  TTS_PROVIDER_OPTIONS: [{ value: "elevenlabs", label: "ElevenLabs" }],
  TTS_MODEL_OPTIONS: [
    { value: "eleven_flash_v2_5", label: "Eleven Flash v2.5" },
    { value: "eleven_multilingual_v2", label: "Eleven Multilingual v2" },
    {
      value: "eleven_v3_conversational",
      label: "Eleven v3 Conversational",
    },
  ],
  LLM_MODEL_OPTIONS: [
    {
      value: "llm_proxy/openai/gpt-4.1-mini",
      label: "LLM Proxy -> OpenAI GPT-4.1 Mini",
    },
    {
      value: "llm_proxy/openai/gpt-5.4-nano",
      label: "LLM Proxy -> OpenAI GPT-5.4 Nano",
    },
    {
      value: "llm_proxy/openai/gpt-5.4-mini",
      label: "LLM Proxy -> OpenAI GPT-5.4 Mini",
    },
    {
      value: "llm_proxy/vertex_ai/gemini-3.5-flash",
      label: "LLM Proxy -> Gemini 3.5 Flash",
    },
    {
      value: "live_inference/qwen3-6-27b-fp8",
      label: "Live Inference -> qwen3-6-27b-fp8",
    },
    {
      value: "cloud_avenue/google/gemma-4-31b-it",
      label: "Cloud Avenue -> gemma-4-31b-it",
    },    
  ],

  // ── Voice options ──
  // Structure: { label, value, language, gender, accent }
  // The display label prefixes each voice with its country flag so voices are
  // grouped visually in the dropdown.
  VOICE_OPTIONS: [
    // French
    {
      value: "YxrwjAKoUKULGd0g8K9Y",
      label: "🇫🇷 Lucie, Support Agent (female, standard accent)",
      language: "French",
      gender: "female",
      accent: "standard",
    },
    {
      value: "YDDaC9XKjODs7hY78qEW",
      label:
        "🇫🇷 Mariane Premium Conversational AI (female, standard accent)",
      language: "French",
      gender: "female",
      accent: "standard",
    },
    {
      value: "FFXYdAYPzn8Tw8KiHZqg",
      label: "🇫🇷 Ingrid (female, standard accent)",
      language: "French",
      gender: "female",
      accent: "standard",
    },
    {
      value: "5OnMHwgTFgvPVwE8jP6B",
      label: "🇫🇷 Anais (female, standard accent)",
      language: "French",
      gender: "female",
      accent: "standard",
    },
    {
      value: "HuLbOdhRlvQQN8oPP0AJ",
      label: "🇫🇷 Claire, Customer Service (female, standard accent)",
      language: "French",
      gender: "female",
      accent: "standard",
    },
    {
      value: "b0Ev8lcOOXx2o9ZcF46H",
      label: "🇫🇷 Martin (male, standard accent)",
      language: "French",
      gender: "male",
      accent: "standard",
    },
    {
      value: "ohItIVrXTBI80RrUECOD",
      label: "🇫🇷 Guillaume (male, standard accent)",
      language: "French",
      gender: "male",
      accent: "standard",
    },
    {
      value: "qEqs132fQmytmOsVez6J",
      label: "🇫🇷 Alain Pro (male, standard accent)",
      language: "French",
      gender: "male",
      accent: "standard",
    },
    {
      value: "TQaDhGYcKI0vrQueAmVO",
      label: "🇫🇷 Lucien (male, standard accent)",
      language: "French",
      gender: "male",
      accent: "standard",
    },
    {
      value: "4LA3XaQgBiuOka0XTJaY",
      label: "🇫🇷 Clement (male, standard accent)",
      language: "French",
      gender: "male",
      accent: "standard",
    },
    {
      value: "HjycCyCVEgEMZADqyGY2",
      label: "🇫🇷 Theron (male, standard accent)",
      language: "French",
      gender: "male",
      accent: "standard",
    },
    {
      value: "GPAQQPp9dazaB2bl4zg9",
      label: "🇫🇷 Victor (male, standard accent)",
      language: "French",
      gender: "male",
      accent: "standard",
    },
    // English
    {
      value: "L0Dsvb3SLTyegXwtm47J",
      label: "🇬🇧 Archer (male, British accent)",
      language: "English",
      gender: "male",
      accent: "British",
    },
    {
      value: "lcMyyd2HUfFzxdCaC4Ta",
      label: "🇬🇧 Lucy, Fresh & Casual (female, British accent)",
      language: "English",
      gender: "female",
      accent: "British",
    },
    {
      value: "1SM7GgM6IMuvQlz2BwM3",
      label:
        "🇬🇧 Mark, Casual Relaxed and Light (male, American accent)",
      language: "English",
      gender: "male",
      accent: "American",
    },
    {
      value: "UgBBYS2sOqTuMpoF3BR0",
      label: "🇬🇧 Mark, Natural Conversations (male, American accent)",
      language: "English",
      gender: "male",
      accent: "American",
    },
    {
      value: "g6xIsTj2HwM6VR4iXFCw",
      label:
        "🇬🇧 Jessica Anne Bogart, Chatty & Friendly (female, American accent)",
      language: "English",
      gender: "female",
      accent: "American",
    },
    {
      value: "c6SfcYrb2t09NHXiT80T",
      label:
        "🇬🇧 Jarnathan, Confident and Versatile (male, American accent)",
      language: "English",
      gender: "male",
      accent: "American",
    },
    {
      value: "OYTbf65OHHFELVut7v2H",
      label: "🇬🇧 Hope (female, American accent)",
      language: "English",
      gender: "female",
      accent: "American",
    },
    {
      value: "56AoDkrOh6qfVPDXZ7Pt",
      label: "🇬🇧 Cassidy (female, American accent)",
      language: "English",
      gender: "female",
      accent: "American",
    },
    // Romanian
    {
      value: "b4bnZ9y3ZRH0myLzE2B5",
      label: "🇷🇴 Robert Mihai (male, standard accent)",
      language: "Romanian",
      gender: "male",
      accent: "standard",
    },
    {
      value: "GRHbHyXbUO8nF4YexVTa",
      label: "🇷🇴 Anca, Warm & Expressive (female, standard accent)",
      language: "Romanian",
      gender: "female",
      accent: "standard",
    },
  ],

  /**
   * Build a greeting from org details.
   * @param {object} org - Organization object with orgname, services, etc.
   * @returns {string}
   */
  buildGreeting(org) {
    const name = (org?.orgname || "").trim();
    const serviceNames = (org?.services || [])
      .map((s) => s.name)
      .filter(Boolean);
    if (!name) return "";
    if (serviceNames.length > 0) {
      return `Hello, this is the AI agent for ${name}. If you need any help with ${serviceNames.join(", ")}, just let me know.`;
    }
    return `Hello, this is the AI agent for ${name}. How can I help you?`;
  },
};
